import React, { useState, useEffect } from 'react'
import { SubmitErrorHandler } from 'react-hook-form'
import { Link, useHistory } from 'react-router-dom'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import { StringParam } from 'use-query-params'
import { NewUserForm, NewUserSchemaType } from 'apps/fleet-planner/components/forms/NewUserForm'
import { getRoute } from 'apps/fleet-planner/constants'
import { useOnboardingWizardContext } from 'apps/fleet-planner/context'
import { useUserAuth, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useQueryParams } from 'utils/hooks'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { ReturningUserSchemaType } from '../forms/ReturningUserForm'
import { OnboardingStepContainer } from './OnboardingStepContainer'

export const OnboardingStepNewUser = () => {
  const { trackEvent } = useFMSAnalytics()
  const { mobileApp } = useOnboardingWizardContext()
  const postUserDetailsToMobileApp = (stringifiedData: string) => {
    postMessageToMobileApp(mobileApp, 'NEW_ACCOUNT_CREATED', stringifiedData)
  }
  const [isLoading, setLoading] = useState<boolean>(false)
  const { createNewUser } = useUserAuth({ onSuccess: postUserDetailsToMobileApp })
  const history = useHistory()
  const [errorMessage, setErrorMessage] = useState<string | undefined>('')
  const [{ token: confirmationToken }] = useQueryParams({
    token: StringParam,
  })

  useEffect(() => {
    trackEvent('Onboarding', 'ONBOARDING_VIEWED_CREATE_ACCOUNT_PAGE')
  }, [trackEvent])

  // scroll to the bottom of the page if an error occurs during account creation on the mobile app
  useEffect(() => {
    if (mobileApp && errorMessage) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    }
  }, [errorMessage, mobileApp])

  // Placeholder routing
  const successHandler = async (data: NewUserSchemaType) => {
    if (isLoading) return
    setLoading(true)
    const { cellPhone = '', firstName = '', lastName = '', password = '', phone = '' } = data || {}
    try {
      await createNewUser({
        confirmationToken,
        password,
        userInfo: {
          firstName,
          lastName,
          phone,
          cellPhone,
        },
      })
      setErrorMessage(undefined)
      trackEvent('Onboarding', 'ONBOARDING_SUCCESSFULLY_SUBMITTED_FORM_ON_CREATE_ACCOUNT_PAGE')
      setLoading(false)
      history.push(getRoute('options'))
    } catch (error) {
      const errors = typeof error === 'string' ? error.replace('Error: ', '') : error.errors

      trackEvent('Onboarding', 'ONBOARDING_FORM_API_ERROR_NEW_USER_PAGE', { errors })
      setErrorMessage(errors)
      setLoading(false)
    }
  }

  const errorHandler: SubmitErrorHandler<ReturningUserSchemaType> = data => {
    trackEvent('Onboarding', 'ONBOARDING_FAILED_TO_SUBMIT_FORM_ON_CREATE_ACCOUNT_PAGE', data)
  }

  const error = !confirmationToken
    ? 'There was an error with your verification link.'
    : errorMessage

  return (
    <OnboardingStepContainer title='Create your Transfix account' maxWidth={424} fadeIn>
      {confirmationToken && (
        <Box pt={1}>
          <NewUserForm
            onSuccess={successHandler}
            onError={errorHandler}
            error={errorMessage}
            isLoading={isLoading}
            isMobileApp={mobileApp}
          />
        </Box>
      )}
      {error && (
        <Box textAlign='center'>
          <Box mb={3}>
            <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={error} fullWidth />
          </Box>
          <Link to={getRoute('emailInput')}>Start over</Link>
        </Box>
      )}
    </OnboardingStepContainer>
  )
}
